export interface IAutomationRule {
  id: number;
  name: string;
  trigger: string;
  actions?: IAutomationRuleAction[];
  conditions?: IAutomationRuleCondition[];
  createdAt: string;
  updatedAt: string;
}

export interface IAutomationRuleAction {
  id: number;
  type: string;
  value: string;
  conditionableType?: any;
  conditionableId?: any;
  actionId?: any;
  createdAt: string;
  updatedAt: string;
}

export interface IAutomationRuleCondition {
  id: number;
  type: string;
  value: string;
  conditionableType: string;
  conditionableId: number;
  createdAt: string;
  updatedAt: string;
}

export enum EAutomationRuleActions {
  ASSIGN_TAG = 'assignTag',
  UPDATE_COLUMN = 'updateColumn',
  AGGREGATE_RELATIONSHIP = 'aggregateRelationship',
  EMAIL = 'email',
  HOLD_ORDER = 'holdOrder',
  SEND_EDI_855 = 'sendEdi855',
  SEND_UNALLOCATED_ORDERS_IN_PURGATORY_EMAIL = 'sendUnallocatedOrdersInPurgatoryEmail',
  SEND_INBOUND_SHIPMENT_EMAIL = 'sendInboundShipmentEmail',
  COMPLETE_ASSEMBLY_ORDER = 'completeAssemblyOrder',
}

export enum EAutomationRuleConditions {
  BOOLEAN = 'boolean',
  DATE = 'date',
  ITEM_COUNT = 'itemCount',
  AGGREGATE_RELATIONSHIP = 'aggregateRelationship',
  HAS_NOT_PERFORMED_ACTION = 'hasNotPerformedAction',
  HAS_TAG = 'hasTag',
  TEXT = 'text',
  ORDER_AGE = 'orderAge',
  ORDER_STATUS = 'orderStatus',
  SHIPPING_METHOD = 'shippingMethod',
  PRODUCT_SKU = 'productSku',
  ORDER_TOTAL = 'orderTotal',
  ORDER_WEIGHT = 'orderWeight',
  APPROVAL_STATUS = 'approvalStatus',
  BOM_ITEM_RECEIVED = 'bomItemReceived',
  BOM_STOCK_AVAILABILITY = 'bomStockAvailability',
}

export enum EAutomationRuleLogicalOperators {
  AND = 'and',
  OR = 'or',
}

export enum EAutomationRuleEvents {
  INVOICE_UPDATED = 'invoice_updated',
  INVOICE_CREATED = 'invoice_created',
  ORDER_UPDATED = 'order_updated',
  ORDER_CREATED = 'order_created',
  DAILY = 'daily',
  HOURLY = 'hourly',
  RECEIVING = 'receiving',
}

/**
 * Enum defining the types of actions that can be performed by automation rules.
 * These action types represent different operations that can be automated in the system,
 * such as assigning tags, updating columns, sending emails, etc.
 */
export enum EAutomationRuleActionTypes {
  ASSIGN_TAG = 'assign_tag',
  UPDATE_COLUMN = 'update_column',
  AGGREGATE_RELATIONSHIP = 'aggregate_relationship',
  EMAIL_NOTIFICATION = 'email_notification',
  HOLD_ORDER = 'hold_order',
  SEND_EDI_855 = 'send_edi_855',
  SEND_UNALLOCATED_ORDERS_IN_PURGATORY_EMAIL = 'send_unallocated_orders_in_purgatory_email',
  SEND_INBOUND_SHIPMENT_EMAIL = 'send_inbound_shipment_email',
  COMPLETE_ASSEMBLY_ORDER = 'complete_assembly_order',
}

/**
 * Enum defining the types of conditions that can be used in automation rules.
 * These condition types represent different criteria that can be evaluated to determine
 * if an automation rule should be triggered, such as boolean checks, dates, item counts, etc.
 */
export enum EAutomationRuleConditionTypes {
  BOOLEAN = 'boolean',
  DATE = 'date',
  ITEM_COUNT = 'item_count',
  AGGREGATE_RELATIONSHIP = 'aggregate_relationship',
  HAS_NOT_PERFORMED_ACTION = 'has_not_performed_action',
  HAS_TAG = 'has_tag',
  TEXT = 'text',
  ORDER_AGE = 'order_age',
  ORDER_STATUS = 'order_status',
  SHIPPING_METHOD = 'shipping_method',
  PRODUCT_SKU = 'product_sku',
  ORDER_TOTAL = 'order_total',
  ORDER_WEIGHT = 'order_weight',
  APPROVAL_STATUS = 'approval_status',
  BOM_ITEM_RECEIVED = 'bom_item_received',
}

/**
 * Event types supported by the automation rule form
 */
export type AutomationEventType = 'order' | 'invoice' | 'receiving' | '';

/**
 * Interface for automation rule condition configuration from AutomationRuleConfig
 */
export interface IAutomationRuleConditionConfig {
  id: string; // String ID matching EAutomationRuleConditions values
  name: string;
  type: string;
}
